import { inject, observer } from 'mobx-react';
import React from 'react';
import { Prompt } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Form,
  FormControl,
  InputGroup,
  Row,
} from 'react-bootstrap';

import { weightsTotalOne } from '../../../models/ensembling';
import ModelBaseNames from '../../../models/modelBase/ModelBaseNames';
import { Threshold } from '../../../models/ModelPerformances';
import { Rule } from '../../../models/rules';
import { incompleteRuleIndexes } from '../../../models/rules/completeness';
import { PROFILES_PERMISSIONS } from '../../../permissions';
import Store from '../../../store';
import checkPermissions from '../../components/CheckPermissions';
import Error from '../../components/error/Error';
import ModelPerformanceBands from '../../components/model/ModelPerformanceBands';
import OverrideRules from '../../components/model/OverrideRules';
import WeightAllocationBar from '../../components/model/WeightAllocationBar';
import PrecisionBars from '../../components/model/PrecisionBars';
import TieringPanel from '../../components/model/TieringPanel';
import DraftNumberInput from '../../components/model/DraftNumberInput';
import {
  fetchLeafGrid,
  fetchPointsGrid,
} from '../../components/model/leafGridApi';
import {
  fetchOverrideImpact,
  RuleImpact,
} from '../../components/model/overrideImpactApi';
import {
  boundsFromThresholds,
  buildLadder,
  LadderRung,
  LeafGrid,
  PointsGrid,
  pointsLadder,
  rangeHint,
  thresholdRange,
  thresholdsFromBounds,
  tiersFromThresholds,
} from '../../components/model/scoreLadder';
import Spinner from '../../components/Spinner';
import { SEGMENT_LABEL } from '../../components/trees/nodeSegment';
import './fineTuning.css';
import {
  onModelRefresh,
  onRulesRefresh,
} from '../../components/model/modelChangeBus';

// Best first, the order every other fit surface stacks them in. `low` has no
// threshold of its own: it is whatever clears none of the three.
const THRESHOLD_SEGMENTS: Threshold[] = ['very good', 'good', 'medium'];

/**
 * How long after the last change the configuration is written.
 *
 * A weight divider fires on every pointer move, so this is what turns a drag
 * into one write rather than forty. Long enough to cover the pause between two
 * nudges of the same handle, short enough that letting go and looking away
 * still saves before you have looked away.
 */
const SAVE_DELAY_MS = 600;

interface FineTuningPageProps {
  store?: Store;
}

interface FineTuningPageState {
  isLoading: boolean;
  /**
   * A write is in flight. Shown rather than hidden: an autosave that says
   * nothing is indistinguishable from a page that dropped the edit.
   */
  isSaving: boolean;
  /**
   * Why the last write failed, if it did. Separate from `ensemblingError`,
   * which belongs to the recompute rather than to saving.
   */
  saveError: string | null;
  /**
   * Why the page could not load, if it could not. `isLoading` clears in a
   * `finally`, so a failed mount lands here and says so instead of leaving the
   * spinner up forever with nothing on screen explaining why.
   */
  loadError: string | null;
  arePerformanceChartsOpen: boolean;
  /** Whether the shipped distribution is folded. */
  areShippedBandsOpen: boolean;
  /**
   * How many rules are too unfinished to send. Held rather than saved, and said
   * out loud, because a page that saves by itself and then silently does not is
   * worse than one that never saved.
   */
  /** Which rules are holding the write, by position. */
  incomplete: number[];
  /**
   * A rule is open and edited and not yet saved.
   *
   * Held as state only so the navigation prompt can observe it; the write
   * itself is driven by the Save button, not by this.
   */
  hasUnsavedRule: boolean;

  /**
   * The joint leaf membership the score ladder is built from. Null once the
   * fetch has settled without one, which is how a non tree model and a failed
   * read both end up on the threshold number inputs.
   */
  leafGrid: LeafGrid | null;
  /**
   * A point based model's ladder source: one row per point total that occurs.
   * Null on a tree based model, and once a fetch has settled without one.
   */
  pointsGrid: PointsGrid | null;
  /**
   * The requested cumulative population shares, best first, and the source of
   * truth for the tier boundaries. Thresholds are derived from these, never the
   * other way round: writing the achieved shares back here is what made the
   * prototype's tiers drift away from the request every time the weights moved.
   */
  bounds: number[] | null;
  /**
   * What each override rule does. Null while unknown or unavailable, which is
   * not the same as an empty list: an empty list means every rule moves nothing,
   * and labelling rules with a zero they did not earn would be worse than saying
   * nothing.
   */
  impacts: RuleImpact[] | null;
  /** The number field holding back what is typed, so it can say why. */
  refusedField: string | null;
  /**
   * The store's tree revision the leaf grid on screen was built for.
   *
   * Every in-app tree edit already wipes this page's models outright. This is
   * for an edit that arrived from elsewhere, an agent for instance. There is no
   * Save and compute button left to point the person at, so instead of stating
   * that the numbers are stale the page refetches the grid, and this is how it
   * notices it has to.
   */
  computedAtRevision: number | null;
}

/**
 * Thresholds and Overrides on one page.
 *
 * The two were separate tabs, which is why the Overrides impact analysis had to
 * be a modal: it existed only to restate the tree weights and thresholds set on
 * the other tab, plus the same chart pair that tab already drew. With both sets
 * of controls and the charts on one page there is nothing left for that modal to
 * do, so it is not carried over.
 *
 * Three cards, in the order the work is done. Tiering, where the readout IS the
 * control: the Leads bar shows where the boundaries fall and its handles are how
 * they move, with the tree weights beside it because re-weighting moves every
 * score underneath those boundaries. Then Precision, the same tiers read as
 * rates. Then Overrides at full width, because a rule is a list of criteria.
 *
 * Everything on the first two cards is derived from one fetch of the leaf grid
 * and recomputed in the browser, so dragging a weight divider moves the tier
 * volumes and the rates as it happens rather than after a round trip.
 *
 * Without a ladder the page falls back to the server's own repartition and the
 * threshold number fields: a model that is not tree based has no leaf columns to
 * group by, and there is nothing to invert a volume against.
 *
 * One decided change is still outstanding. Per-rule impact counts need
 * attribution that does not exist: `getOverridesAsSQLQueries` collapses every
 * rule into three merged expressions, so nothing today ties a moved lead to the
 * rule that moved it.
 */
export default inject('store')(
  observer(
    class FineTuningPage extends React.Component<
      FineTuningPageProps,
      FineTuningPageState
    > {
      /** Pending debounced write, if any. */
      saveTimer: ReturnType<typeof setTimeout> | null;

      /**
       * Coalescing, so two writes can never land out of order.
       *
       * Each write sends the whole configuration and the last one to arrive
       * wins, so overlapping requests can persist a state the user already moved
       * away from. Rather than queue them, a change arriving mid-flight sets
       * `saveAgain` and the in-flight write re-runs itself on completion with
       * whatever the current values are by then.
       */
      saveInFlight: boolean;

      saveAgain: boolean;

      /** Guards the tree-revision refetch against re-entering itself. */
      refreshingGrid: boolean;

      /**
       * What is waiting to be written. Tracked separately because the two are
       * committed by different gestures: the configuration on a settled drag,
       * the rules when focus leaves the Overrides card. One timer can carry
       * both, and the impact query at the end reads both.
       */
      configDirty: boolean;

      rulesDirty: boolean;

      /**
       * How many rules there were last time this page looked.
       *
       * Remembered rather than compared against the store, because `createRule`
       * pushes onto `model.rules` itself and hands the SAME array back, so by
       * the time the callback runs the store already holds the new rule and
       * comparing the two lengths compares an array to itself. Nothing in the
       * types says so and nothing in the tests catches it: the add would simply
       * never commit.
       */
      lastRuleCount: number | null;

      /**
       * Which impact request is the current one.
       *
       * Four call sites, none of them serialised against the others, so the
       * last response to arrive was winning rather than the last one asked for.
       */
      impactsSeq: number;

      mounted: boolean;

      constructor(props: FineTuningPageProps) {
        super(props);
        this.changeWeights = this.changeWeights.bind(this);
        this.changeThreshold = this.changeThreshold.bind(this);
        this.autoEnsembling = this.autoEnsembling.bind(this);
        this.isWeightsNotEqual100 = this.isWeightsNotEqual100.bind(this);
        this.updateOverrideRules = this.updateOverrideRules.bind(this);
        this.saveOverrideRules = this.saveOverrideRules.bind(this);
        this.togglePerformanceCharts = this.togglePerformanceCharts.bind(this);
        this.toggleShippedBands = this.toggleShippedBands.bind(this);
        this.changeBounds = this.changeBounds.bind(this);
        this.loadImpacts = this.loadImpacts.bind(this);
        this.load = this.load.bind(this);
        this.scheduleSave = this.scheduleSave.bind(this);
        this.persist = this.persist.bind(this);
        this.saveTimer = null;
        this.saveInFlight = false;
        this.saveAgain = false;
        this.refreshingGrid = false;
        this.configDirty = false;
        this.rulesDirty = false;
        this.impactsSeq = 0;
        this.lastRuleCount = null;
        this.mounted = true;
        this.state = {
          isLoading: true,
          isSaving: false,
          saveError: null,
          loadError: null,
          arePerformanceChartsOpen: true,
          areShippedBandsOpen: false,
          incomplete: [],
          hasUnsavedRule: false,
          leafGrid: null,
          pointsGrid: null,
          bounds: null,
          impacts: null,
          refusedField: null,
          computedAtRevision: null,
        };
      }

      async componentDidMount() {
        // The agent wrote the thresholds, switched the base, or changed the rules a
        // points model's grid is built from. Re-read what the page draws, unless the
        // person's own drag is still waiting to save: theirs is the newer intent.
        const reload = () => {
          if (this.saveTimer || this.state.isSaving) return;
          this.reloadFromServer().catch(() => undefined);
        };
        this.unsubscribes = [onModelRefresh(reload), onRulesRefresh(reload)];
        try {
          await this.load();
        } catch (error) {
          this.setState({
            loadError:
              error instanceof Error
                ? error.message
                : 'The fine tuning page could not be loaded.',
          });
        } finally {
          // Always, so no failure above can strand the page on the spinner.
          this.setState({ isLoading: false });
        }
      }

      /**
       * A pending write is sent, not dropped.
       *
       * Navigating away within the debounce window is the ordinary way to finish
       * editing, so treating it as a cancel would lose the last change of every
       * session. Fired without awaiting because unmount cannot wait; the request
       * is already on the wire by the time this returns.
       */
      unsubscribes: Array<() => void> = [];

      componentWillUnmount() {
        this.mounted = false;
        this.unsubscribes.forEach((unsubscribe) => unsubscribe());
        if (this.saveTimer) {
          clearTimeout(this.saveTimer);
          this.saveTimer = null;
        }
        // Only the configuration. A weight or a boundary is written by the act
        // of moving it, so a drag that unmounted before its timer fired is an
        // edit the person believes is saved.
        //
        // A rule is not: it is written by its own Save button, and flushing an
        // unsaved one here would write the half-typed rule that the button
        // exists to hold back. The `Prompt` above asks first instead.
        this.rulesDirty = false;
        if (this.configDirty) void this.persist();
      }

      /**
       * A tree changed underneath the page.
       *
       * The leaf grid describes the trees it was built from, so an edit that
       * arrived from elsewhere makes every number on this page describe a model
       * that no longer exists. This used to be a sentence asking the person to
       * press Save and compute; with that button gone the page fixes it itself.
       */
      componentDidUpdate() {
        const { computedAtRevision } = this.state;
        const { treeRevision } = this.props.store;
        if (
          computedAtRevision === null ||
          treeRevision === computedAtRevision ||
          this.refreshingGrid
        ) {
          return;
        }
        this.refreshingGrid = true;
        void this.refreshGrid().finally(() => {
          this.refreshingGrid = false;
        });
      }

      /**
       * Rebuild the ladder for the tree as it is now, keeping the requested
       * volumes. The bounds are what the person asked for and a changed tree
       * does not change the request, only the scores that satisfy it.
       */
      async refreshGrid() {
        const { store } = this.props;
        const { ensemblingPage } = store;
        const revision = store.treeRevision;
        const grids = await this.fetchGrids();
        const { ensemble } = ensemblingPage;
        this.setState({ ...grids, computedAtRevision: revision });
        if (ensemble && this.state.bounds) {
          this.applyBounds(this.state.bounds, ensemble.weights, grids);
        }
        await this.loadImpacts();
      }

      /**
       * The stored configuration and the grid again, with the bounds re-derived from
       * the stored thresholds exactly as the page opens on them.
       */
      async reloadFromServer() {
        const { store } = this.props;
        const { tenant, modelId, ensemblingPage } = store;
        // The override list too: an agent adding, capping or removing one changes what
        // the rules card on this page lists, and the store's copy is the one it loaded.
        await Promise.all([
          ensemblingPage.loadConfiguration(tenant, modelId),
          store.overridesPage.get({ tenant, modelId }),
        ]);
        this.lastRuleCount = store.overridesPage.rules.length;
        const grids = await this.fetchGrids();
        const { ensemble } = ensemblingPage;
        const ladder = ensemble ? this.ladderFor(ensemble.weights, grids) : [];
        if (!this.mounted) return;
        this.setState({
          ...grids,
          bounds: ladder.length
            ? boundsFromThresholds(ensemble.thresholds, ladder)
            : null,
        });
        await this.loadImpacts();
      }

      /**
       * Everything the page needs before it can draw. Split out so the mount can
       * wrap the whole sequence in one try, rather than each await guarding
       * itself and the last one owning `isLoading`.
       */
      async load() {
        const { store } = this.props;
        const { tenant, modelId, activeModel, ensemblingPage } = store;

        // Both halves of the page need the trees and the form fields, so they
        // are fetched once here rather than twice by two mounting pages.
        await Promise.all([
          store.loadAllTrees(),
          store.fetchFormFields(),
          store.overridesPage.get({ tenant, modelId }),
          ensemblingPage.ensemble
            ? Promise.resolve()
            : ensemblingPage.loadConfiguration(tenant, modelId),
        ]);

        this.lastRuleCount = store.overridesPage.rules.length;

        // The grid first, and the server-side repartition only if it does not
        // arrive.
        //
        // This used to run `loadEnsemblingPerformances` unconditionally before
        // the grid was even requested. On a tree model that is a full scoring
        // run, measured at 2.4s cold, producing `ensemble.performances`, which
        // is rendered ONLY on the no-ladder fallback path: with a grid the
        // tiering and precision are derived in the browser and nothing ever
        // reads it. Every cold mount paid for a number it did not draw.
        const revision = store.treeRevision;
        const [grids] = await Promise.all([
          this.fetchGrids(),
          this.loadImpacts(),
          // What actually ships: the only reading on this page with the
          // overrides applied. The leaf grid is scored before they are, and
          // `calculateEnsemblingMain` asks for `withOverrides: false`. Fetched
          // alongside the others rather than before them, because a cold
          // with-overrides cache is a full scoring run and serialising it would
          // add that to every mount.
          // Caught here as well as inside, like `fetchLeafGrid` above: this
          // card is one of three readings on the page and none of them is
          // worth the other two.
          store.loadOverrideEnsembleData().catch(() => null),
        ]);
        // Read after the awaits rather than destructured at the top, and guarded:
        // `render` refuses to draw without an ensemble, and deriving bounds from
        // one that never arrived would throw here instead, where nothing catches
        // it per statement.
        const { ensemble } = ensemblingPage;
        const ladder = ensemble ? this.ladderFor(ensemble.weights, grids) : [];
        this.setState({
          ...grids,
          // Opened on what the model already does. Deriving the bounds from the
          // stored thresholds rather than defaulting them is what stops the
          // control moving a live model's cut points just by being looked at.
          bounds: ladder.length
            ? boundsFromThresholds(ensemble.thresholds, ladder)
            : null,
          computedAtRevision: revision,
        });

        // No grid, so no ladder: a model that is not tree based has no leaf
        // columns to group by, and its repartition can only come from the
        // recomputing route. This is the one case that still needs it, and it
        // is now reached only when it is actually going to be rendered.
        if (
          !ladder.length &&
          activeModel.type === 'customer_fit' &&
          !this.isWeightsNotEqual100()
        ) {
          await store.loadEnsemblingPerformances({ isProcessing: true });
        }
      }

      togglePerformanceCharts() {
        this.setState((state) => ({
          arePerformanceChartsOpen: !state.arePerformanceChartsOpen,
        }));
      }

      toggleShippedBands() {
        this.setState((state) => ({
          areShippedBandsOpen: !state.areShippedBandsOpen,
        }));
      }

      /**
       * The whole allocation at once. A divider moves two trees together, and
       * writing them one at a time would put the model through a state where the
       * weights do not total 100, which anything observing them would see.
       */
      changeWeights(weights: Record<string, number>) {
        const { ensemble } = this.props.store.ensemblingPage;
        Object.keys(weights).forEach((tree) => {
          ensemble.weights[tree] = weights[tree];
        });
        // Re-weighting moves every score, so the thresholds that delivered the
        // requested volumes no longer do. They are re-derived from the SAME
        // requested bounds, which is what makes a volume intent survive a weight
        // change instead of drifting with it.
        this.applyBounds(this.state.bounds, weights);
        this.scheduleSave({ config: true });
      }

      changeBounds(bounds: number[]) {
        this.applyBounds(
          bounds,
          this.props.store.ensemblingPage.ensemble.weights
        );
        this.setState({ bounds });
        this.scheduleSave({ config: true });
      }

      /**
       * Requested volumes to stored thresholds. The only writer of
       * `ensemble.thresholds` while the volume control is in use.
       *
       * Takes the grid rather than always reading state, because the caller that
       * has just refetched one cannot see it there yet: `setState` has not
       * flushed, and deriving from the previous grid would place the boundaries
       * on a tree that no longer exists.
       */
      applyBounds(
        bounds: number[] | null,
        weights: Record<string, number>,
        grids?: Pick<FineTuningPageState, 'leafGrid' | 'pointsGrid'>
      ) {
        const ladder = this.ladderFor(weights, grids);
        if (!bounds || !ladder.length) return;
        const derived = thresholdsFromBounds(bounds, ladder);
        if (!derived) return;
        const { ensemble } = this.props.store.ensemblingPage;
        Object.keys(derived).forEach((segment) => {
          ensemble.thresholds[segment] = derived[segment];
        });
      }

      changeThreshold(threshold: string, value: number) {
        const { ensemble } = this.props.store.ensemblingPage;
        ensemble.thresholds[threshold] = value;
        // A typed threshold is the request here, so the handles follow it. The
        // other way round, as `applyBounds` does, would snap the value just
        // typed onto the nearest rung under the person typing it.
        const ladder = this.ladderFor(ensemble.weights);
        if (ladder.length) {
          this.setState({
            bounds: boundsFromThresholds(ensemble.thresholds, ladder),
          });
        }
        this.scheduleSave({ config: true });
      }

      /**
       * Note what changed and restart the clock.
       *
       * One timer for both kinds of edit. A rule committed while a weight is
       * still settling should not produce two writes and two impact queries when
       * the person made one change.
       */
      scheduleSave(what: { config?: boolean; rules?: boolean }) {
        if (what.config) this.configDirty = true;
        if (what.rules) this.rulesDirty = true;
        if (this.saveTimer) clearTimeout(this.saveTimer);
        this.saveTimer = setTimeout(() => {
          this.saveTimer = null;
          void this.persist();
        }, SAVE_DELAY_MS);
      }

      /**
       * Write whatever is pending, once, without overlapping itself.
       *
       * A change arriving mid-flight sets `saveAgain` rather than starting a
       * second request, and the loop re-reads the current values on the next
       * pass. Two concurrent writes would each send the whole configuration and
       * the slower one would win, which is how autosave silently reverts an edit.
       */
      async persist() {
        if (this.saveInFlight) {
          this.saveAgain = true;
          return;
        }
        this.saveInFlight = true;
        if (this.mounted) this.setState({ isSaving: true, saveError: null });
        try {
          do {
            this.saveAgain = false;
            await this.writeOnce();
          } while (this.saveAgain);
        } catch (error) {
          // Every call inside `writeOnce` records its own failure, so reaching
          // here means something unforeseen did not. Without this the rejection
          // escapes a `void this.persist()` and the page carries on claiming to
          // be saved.
          if (this.mounted) {
            this.setState({
              saveError:
                error instanceof Error
                  ? error.message
                  : 'Your last change could not be saved.',
            });
          }
        } finally {
          this.saveInFlight = false;
          if (this.mounted) this.setState({ isSaving: false });
        }
      }

      async writeOnce() {
        const { store } = this.props;
        // Whether anything reached the server. Both branches below have a
        // refusal path, and a refusal changes nothing, so the refetch at the
        // end has nothing to go and read.
        let wrote = false;

        // Held, not sent. `setOverrides` validates by executing, so one
        // unfinished rule fails the request and rolls back the finished ones
        // with it. Left dirty so the save happens as soon as the rule is
        // finished, rather than being dropped.
        const unfinished = incompleteRuleIndexes(store.overridesPage.rules);
        if (this.rulesDirty && unfinished.length) {
          if (this.mounted) this.setState({ incomplete: unfinished });
        } else if (this.rulesDirty) {
          this.rulesDirty = false;
          if (this.mounted) this.setState({ incomplete: [] });
          store.analyticsTrackEvent('Updated override');
          // No re-read: the PUT returns the sorted list, which is the only
          // thing that read was for.
          await store.saveOverrideRules();
          wrote = true;
          this.lastRuleCount = store.overridesPage.rules.length;
          const { savingError } = store.overridesPage;
          if (savingError && this.mounted) {
            this.setState({ saveError: savingError.message });
          }
          // Free here, and only here. `setOverrides` invalidates the
          // with-overrides cache and then immediately recomputes it, so by the
          // time the write returns the answer is already sitting in Redis.
        }

        if (this.configDirty) {
          this.configDirty = false;
          if (this.isWeightsNotEqual100()) {
            // The same rule the Save button enforced by being disabled. The
            // warning is already on screen; writing anyway would persist an
            // allocation every other surface refuses.
            if (this.mounted) {
              this.setState({
                saveError:
                  'These weights do not total 100%, so nothing was saved.',
              });
            }
          } else {
            store.analyticsTrackEvent('Updated ensembling');
            if (this.ladderFor(store.ensemblingPage.ensemble.weights).length) {
              // The grid is what the tiering and precision on screen were
              // derived from, so there is nothing left for the server to compute.
              await store.saveEnsemblingConfiguration();
            } else {
              // No grid, so no ladder and nothing to compute in the browser. A
              // model without trees has no leaf columns to group by, and the
              // repartition can only come from the recomputing route.
              await store.loadEnsemblingPerformances({ isProcessing: false });
            }
            wrote = true;
            if (this.mounted) {
              this.setState({ computedAtRevision: store.treeRevision });
            }
          }
        }

        // Last, and unconditionally, both of them. A rule's reach depends on the
        // thresholds as much as on its own conditions, so either kind of write
        // invalidates the impact counts AND the shipped distribution.
        //
        // The Recompute button that used to sit on the shipped card is gone. It
        // existed to avoid paying for a with-overrides run on every settled drag,
        // which was a real cost honestly stated and still a button on a page
        // whose whole point is not having any. The card is folded by default, so
        // the cost is paid where nobody is waiting on it.
        //
        // Only when something was written, though. Both branches above can
        // refuse, and blurring out of Overrides with a half-typed rule is the
        // ordinary way to reach this: unconditional, that gesture bought a full
        // with-overrides scoring run to re-read numbers that had not moved.
        if (!wrote) return;
        await Promise.all([
          this.loadImpacts(),
          store.loadOverrideEnsembleData(),
        ]);
      }

      async autoEnsembling() {
        const { store } = this.props;
        await store.autoEnsembling({ isProcessing: false });
        // Auto-select replaces the thresholds outright, so the volumes the
        // handles were requesting are not what the model does any more. Read the
        // bounds back off what it chose rather than leaving the old request to
        // clamp the next drag against boundaries nobody asked for.
        const { ensemble } = store.ensemblingPage;
        const ladder = ensemble ? this.ladderFor(ensemble.weights) : [];
        if (ladder.length) {
          this.setState({
            bounds: boundsFromThresholds(ensemble.thresholds, ladder),
            computedAtRevision: store.treeRevision,
          });
        }
        await this.loadImpacts();
      }

      /**
       * Whether the allocation is one the save route would refuse.
       *
       * Shares the route's own tolerance rather than testing the total against
       * 100 exactly. Exact equality disagreed with the route on 12 of the 5151
       * integer three-way allocations, and on this page a disagreement is not a
       * greyed-out button, it is a drag that renders and is then dropped.
       */
      isWeightsNotEqual100() {
        const { activeModel, ensemblingPage } = this.props.store;
        // A point based model's weights are whatever it last held as a tree
        // model and score nothing, so they cannot make a save wrong.
        if (activeModel.base !== ModelBaseNames.trees) return false;
        const { ensemble } = ensemblingPage;
        return Boolean(ensemble?.weights) && !weightsTotalOne(ensemble.weights);
      }

      /**
       * The grids this model's ladder is built from, fetched by base.
       *
       * Only the one that applies is asked for. The leaf grid route answers a
       * point based model with a 400, which used to be the way this page found
       * out it had no ladder.
       */
      async fetchGrids(): Promise<
        Pick<FineTuningPageState, 'leafGrid' | 'pointsGrid'>
      > {
        const { tenant, modelId, activeModel, treeRevision } = this.props.store;
        if (activeModel.base === ModelBaseNames.trees) {
          const leafGrid = await fetchLeafGrid(
            tenant,
            modelId,
            treeRevision
          ).catch(() => null);
          return { leafGrid, pointsGrid: null };
        }
        return {
          leafGrid: null,
          pointsGrid: await fetchPointsGrid(tenant, modelId),
        };
      }

      /**
       * Every score that occurs, best first, whatever the base. The volume
       * control, the tiers and the precision are all read off this, so a point
       * based model gets the same live tiering a tree based one does.
       */
      ladderFor(
        weights: Record<string, number>,
        grids: Pick<FineTuningPageState, 'leafGrid' | 'pointsGrid'> = this.state
      ): LadderRung[] {
        if (this.props.store.activeModel.base === ModelBaseNames.trees) {
          return grids.leafGrid ? buildLadder(grids.leafGrid, weights) : [];
        }
        return pointsLadder(grids.pointsGrid);
      }

      /**
       * Record the edit and nothing else.
       *
       * `RuleEditor` calls this on every keystroke inside a condition, and a rule
       * is a piece of SQL: writing a half-typed one reaches `setOverrides`, which
       * validates it by running it, fails, and rolls back. So the edit is held
       * here and committed when focus leaves the card.
       */
      async updateOverrideRules(newRules: Rule[]) {
        const { store } = this.props;
        // A DELETED rule is a finished action and commits at once. A CREATED one
        // is not: a fresh rule has an empty complement, and committing it sent
        // `invalid input syntax for type numeric: ""` back from the server
        // before the person had typed anything. Creation is the START of an
        // edit, so it waits for focus to leave the card like every other edit.
        const removed =
          this.lastRuleCount !== null && newRules.length < this.lastRuleCount;
        this.lastRuleCount = newRules.length;
        await store.updateOverrideRules(newRules);
        this.rulesDirty = true;
        if (removed) this.scheduleSave({ rules: true });
      }

      /**
       * Write the rules now, because a Save button was pressed.
       *
       * This replaced a blur listener on the whole card, resolved against
       * `document.activeElement` a tick later. That worked, and no reader
       * could have known it existed: the gesture that saved was clicking
       * somewhere that was not a rule.
       *
       * Immediate rather than debounced. The delay exists so a weight divider
       * firing on every pointer move produces one write, and a button press is
       * already one gesture. A pending timer is cleared instead of racing it,
       * and whatever it was holding goes out with this write.
       *
       * Reports whether the rules reached the server, which is what decides
       * whether the row that asked closes. An unfinished rule is refused by
       * `writeOnce` and leaves `rulesDirty` set, so the answer is no and the
       * card's own note names which rule is holding it.
       */
      async saveOverrideRules(): Promise<boolean> {
        this.rulesDirty = true;
        if (this.saveTimer) {
          clearTimeout(this.saveTimer);
          this.saveTimer = null;
        }
        await this.persist();
        const { savingError } = this.props.store.overridesPage;
        return !this.rulesDirty && !savingError;
      }

      /**
       * Refetched rather than derived, because the counts depend on the
       * thresholds as well as on the rules: a ceiling rule only moves a lead
       * whose score clears it, so moving a boundary changes what the same rule
       * does without the rule changing at all.
       */
      async loadImpacts() {
        const { tenant, modelId, activeModel } = this.props.store;
        // Attribution is a CASE over node columns, so a point based model has
        // none to give and the route answers every one of its rules with an
        // empty list. Not asked, rather than asked and misread as "no leads".
        if (activeModel.base !== ModelBaseNames.trees) return;
        this.impactsSeq += 1;
        const seq = this.impactsSeq;
        const impacts = await fetchOverrideImpact(tenant, modelId);
        // A slow earlier request must not overwrite a fresher answer, and the
        // unmount flush reaches this after `mounted` goes false.
        if (this.mounted && seq === this.impactsSeq) this.setState({ impacts });
      }

      /**
       * One labelled number, the same shape in both control cards. Weights and
       * thresholds had drifted into two slightly different Bootstrap grid rows,
       * which is why their inputs did not line up down the column.
       */
      numberField({
        id,
        label,
        value,
        step,
        suffix,
        min,
        max,
        hint,
        onChange,
      }: {
        id: string;
        label: string;
        value: number;
        step?: string;
        suffix: string;
        /** Inclusive. A value outside is shown while typed but not committed. */
        min?: number;
        max?: number;
        /** Said under the field while it holds back a value outside the range. */
        hint?: string;
        onChange: (value: number) => void;
      }) {
        const { isAllowedToEdit } = this.props.store;
        return (
          <div className="ftune-field" key={id}>
            <label className="ftune-field-label" htmlFor={id}>
              {label}
            </label>
            <InputGroup size="sm" className="ftune-input">
              {/* A draft, not a plain controlled field: a number input reports
                  '' until what is typed parses, so a lone '-' or a cleared
                  field read as 0 and autosave wrote it, and refusing those
                  instead froze the field. */}
              <FormControl
                as={DraftNumberInput}
                id={id}
                step={step}
                min={min}
                max={max}
                value={value}
                disabled={!isAllowedToEdit}
                onCommit={onChange}
                onRefusedChange={(refused: boolean) =>
                  this.setState((state) => ({
                    refusedField: refused
                      ? id
                      : state.refusedField === id
                      ? null
                      : state.refusedField,
                  }))
                }
              />
              <InputGroup.Text>{suffix}</InputGroup.Text>
            </InputGroup>
            {hint && this.state.refusedField === id && (
              <p className="ftune-field-hint" role="status">
                {hint}
              </p>
            )}
          </div>
        );
      }

      render() {
        const { store } = this.props;
        const {
          activeModel,
          ensemblingPage,
          formFields,
          isAllowedToEdit,
          overridesPage,
          treesPage,
        } = store;
        const {
          ensemble,
          ensemblingError,
          fetchingBestThresholds,
          loadingEnsemblingPerformances,
        } = ensemblingPage;

        if (this.state.isLoading) return <Spinner />;
        // Loading has finished. Either of these used to fall through to the
        // spinner and sit there.
        if (this.state.loadError)
          return <Error message={this.state.loadError} />;
        if (!ensemble)
          return (
            <Error message="This model's ensembling configuration could not be loaded." />
          );

        const isTreeBased = activeModel.base === ModelBaseNames.trees;
        const weightsNotEqual100 = this.isWeightsNotEqual100();

        // The with-overrides run, which is a different fetch from everything
        // else here. `EnsemblePerformance` nests the actual figures one level
        // down, so this unwraps to what the band charts take.
        const { loadingEnsemble } = overridesPage;
        const shippedPerformances =
          overridesPage.ensemble?.performances?.performances ?? null;

        // Both derived, never stored. The ladder is a function of the grid and
        // the current weights, and the tiers are a function of the ladder and
        // the thresholds actually in the model, so neither can fall out of step
        // with what would be saved.
        const { bounds, leafGrid, computedAtRevision } = this.state;
        const staleBy =
          computedAtRevision === null
            ? 0
            : store.treeRevision - computedAtRevision;
        const isStale = staleBy > 0;
        const ladder = this.ladderFor(ensemble.weights);
        const tierOutcomes =
          bounds && ladder.length
            ? tiersFromThresholds(ensemble.thresholds, ladder)
            : null;

        // A point threshold steps by one because rules are usually written in
        // whole points. Any number can still be typed.
        const thresholdStep = isTreeBased ? 0.1 : 1;
        const thresholds = THRESHOLD_SEGMENTS.map((threshold) =>
          this.numberField({
            id: `threshold_${threshold}`,
            // The segment names as the rest of the product writes them. This
            // read "very good" in lower case, which matched nothing else on
            // screen, least of all the chart legend directly above it.
            label: SEGMENT_LABEL[threshold],
            value: ensemble.thresholds[threshold],
            step: String(thresholdStep),
            suffix: isTreeBased ? '%' : 'points',
            // Typed point thresholds keep their tiers apart, as the volume
            // handles do. Tree thresholds keep accepting equal values, which
            // Auto-select produces on purpose for a model with no Good.
            ...(isTreeBased
              ? {}
              : {
                  ...thresholdRange(
                    threshold as Exclude<Threshold, 'low'>,
                    ensemble.thresholds,
                    thresholdStep
                  ),
                  hint: rangeHint(
                    threshold as Exclude<Threshold, 'low'>,
                    ensemble.thresholds
                  ),
                }),
            onChange: (value) => this.changeThreshold(threshold, value),
          })
        );

        // One action group, rendered wherever the controls that need it end up.
        // A live model has nothing to change from here.
        //
        // Save and compute is gone. Every control on this page writes itself,
        // and the numbers beside them were never what that button computed: they
        // are derived from the leaf grid as the handle moves. What is left is the
        // one action that is genuinely a request rather than an edit.
        // Always rendered, hidden when idle. Mounting it on save reflowed the row
        // under the pointer every time a divider settled, which is a control
        // moving itself while being used.
        const saving = (
          <span
            className={`ftune-saving ${
              this.state.isSaving ? '' : 'ftune-saving--idle'
            }`}
          >
            Saving...
          </span>
        );
        const actions = !activeModel.live && (
          <div className="ftune-actions">
            {checkPermissions(
              PROFILES_PERMISSIONS.ARCHITECT,
              <Button
                size="sm"
                className="ftune-btn-quiet"
                onClick={this.autoEnsembling}
                disabled={
                  loadingEnsemblingPerformances || fetchingBestThresholds
                }
              >
                {fetchingBestThresholds
                  ? 'Fetching best parameters...'
                  : 'Auto-select parameters'}
              </Button>
            )}
            {saving}
          </div>
        );

        return (
          <div className="ftune mt-3 mb-5">
            {/* A rule is written by its own Save button, so leaving with one
                open and edited discards it. The weights and boundaries above
                are not on this prompt: those write themselves. */}
            <Prompt
              when={this.state.hasUnsavedRule}
              message="This override rule has changes you have not saved. Leave anyway?"
            />

            <Error message={ensemblingError} />

            {/* The tree moved under the page, so the grid these numbers came
                from describes trees that no longer exist. This used to ask the
                person to press Save and compute; `componentDidUpdate` refetches
                instead, and this says so while that is in flight. */}
            {isStale && (
              <p className="ftune-stale">
                The tree changed {staleBy === 1 ? 'once' : `${staleBy} times`}{' '}
                while this page was open. Bringing these numbers up to date.
              </p>
            )}

            {this.state.saveError && <Error message={this.state.saveError} />}

            {loadingEnsemblingPerformances && <Spinner />}

            {tierOutcomes && !loadingEnsemblingPerformances && (
              <>
                <Card className="ftune-card">
                  <Card.Body>
                    <h5 className="ftune-title">Tiering</h5>
                    {/* Above the tiering, not beside it. Re-weighting moves
                        every score underneath the boundaries, so it is the first
                        thing done and the boundaries are set against its result.
                        In the right-hand column it read as an aside to the chart
                        rather than as the step before it. */}
                    {/* The allocation and the only action on the card share a
                        row. Auto-select rewrites the weights and the thresholds,
                        so it belongs beside the control it rewrites rather than
                        stranded under the conversions bar, three charts away
                        from anything it touches. */}
                    {isTreeBased && (
                      <div className="ftune-weights">
                        <div className="ftune-weights-bar">
                          <p className="tier-barlab">Tree weights</p>
                          <WeightAllocationBar
                            weights={ensemble.weights}
                            onChange={this.changeWeights}
                            disabled={!isAllowedToEdit}
                          />
                        </div>
                        {actions}
                      </div>
                    )}
                    {isTreeBased && weightsNotEqual100 && (
                      <p className="ftune-warn">
                        These weights do not total 100%. Move a divider to fix
                        it.
                      </p>
                    )}

                    <TieringPanel
                      tiers={tierOutcomes}
                      bounds={bounds}
                      ladder={ladder}
                      onChangeBounds={this.changeBounds}
                      disabled={!isAllowedToEdit}
                      // On a point based model the thresholds are written in
                      // points, which is how a person reasons about rules, so
                      // each boundary carries its own under its handle and
                      // every keystroke redraws the bars above it.
                      thresholdInput={
                        isTreeBased
                          ? undefined
                          : {
                              step: thresholdStep,
                              suffix: 'pts',
                              onChange: this.changeThreshold,
                            }
                      }
                    />

                    {/* No Auto-select: a point based model's thresholds are
                        typed under the handles, and a button that rewrites all
                        three at once would overwrite what was just typed. */}
                    {!isTreeBased && !activeModel.live && (
                      <div className="ftune-actions">{saving}</div>
                    )}
                    {/* Precision belongs to the control, not beside it. These
                        are the same four tiers the bar above draws, read as
                        rates instead of volumes, and they answer the question
                        the handles raise: what did moving that boundary buy. In
                        its own card it read as a third opinion on the page. */}
                    <h6 className="ftune-subtitle">Precision</h6>
                    <p className="ftune-hint">
                      Percentages are conversion rates.
                    </p>
                    <PrecisionBars tiers={tierOutcomes} />
                  </Card.Body>
                </Card>
              </>
            )}

            {/* No ladder: the server's own repartition, and thresholds as scores.
                Reached when the grid for this model's base failed to load or came
                back empty, so there is nothing to invert a volume against. */}
            {!tierOutcomes && !loadingEnsemblingPerformances && (
              <>
                <div className="ftune-card">
                  {ensemble.performances && (
                    <ModelPerformanceBands
                      performances={ensemble.performances.performances}
                      open={this.state.arePerformanceChartsOpen}
                      onToggleOpen={this.togglePerformanceCharts}
                      scope={isTreeBased ? undefined : 'Rule points'}
                    />
                  )}
                </div>
                <Row>
                  {isTreeBased && (
                    <Col sm={5}>
                      <Card className="ftune-card">
                        <Card.Body>
                          <h5 className="ftune-title">Tree Weights</h5>
                          <WeightAllocationBar
                            weights={ensemble.weights}
                            onChange={this.changeWeights}
                            disabled={!isAllowedToEdit}
                          />
                          {weightsNotEqual100 && (
                            <p className="ftune-warn">
                              These weights do not total 100%. Move a divider to
                              fix it.
                            </p>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  )}
                  <Col sm={isTreeBased ? 4 : 6}>
                    <Card className="ftune-card">
                      <Card.Body>
                        <h5 className="ftune-title">Segment Thresholds</h5>
                        <p className="ftune-hint">
                          The lowest score that still earns each segment
                        </p>
                        <Form>{thresholds}</Form>
                        {actions}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </>
            )}

            {/* Each rule carries its own Save. The card used to commit on
                `onBlur`, which wrote whenever focus left it: correct, and
                invisible, since the gesture that saved was clicking somewhere
                that was not a rule. */}
            <Card className="ftune-card">
              <Card.Body>
                <h5 className="ftune-title">Overrides</h5>
                {/* A page that saves by itself and then silently does not is
                    worse than one that never saved, so the hold is stated. */}
                {this.state.incomplete.length > 0 && (
                  <p className="ftune-warn">
                    {/* Named, not counted. One unfinished rule holds every other
                        rule's write, so "one rule is unfinished" on a card of
                        nine sends the reader hunting. The positions were always
                        computed; only the count was being kept. */}
                    {this.state.incomplete.length === 1
                      ? `Rule ${
                          this.state.incomplete[0] + 1
                        } is unfinished, so nothing is saved yet.`
                      : `Rules ${this.state.incomplete
                          .map((index) => index + 1)
                          .join(
                            ', '
                          )} are unfinished, so nothing is saved yet.`}{' '}
                    Fill in every condition to save.
                  </p>
                )}
                {/* The fit-specific list, not `RuleEditor`. Grouped by the
                    `should` that decides precedence, collapsed to one line per
                    rule, and with no SQL mode: an override is a business rule
                    and the fifteen operators express one without SQL. It opens
                    into the same `RuleComponent`, so nothing about editing a
                    rule is rewritten, and `RuleEditor` keeps serving the
                    unflagged Overrides page and the aggregations wizard
                    unchanged. */}
                <OverrideRules
                  impacts={this.state.impacts}
                  // The denominator for "moves 4% of leads". The grid's own
                  // population is the whole scored set, which is what a rule's
                  // reach should be read against.
                  impactTotal={leafGrid?.population}
                  impactConversions={leafGrid?.conversions}
                  model={overridesPage}
                  formFields={formFields}
                  updateRules={this.updateOverrideRules}
                  saveRules={this.saveOverrideRules}
                  onDirtyChange={(dirty) =>
                    this.setState({ hasUnsavedRule: dirty })
                  }
                  modelType={activeModel.type}
                  isAllowedToEdit={isAllowedToEdit}
                />
              </Card.Body>
            </Card>

            {/* What ships, and last, because it is the effect of everything
                above it.

                The Tiering handles act on the blended tree score, so the ladder
                they move along is built from the leaf grid, which is scored
                BEFORE overrides are applied, and `calculateEnsemblingMain` asks
                for `withOverrides: false` too. So this is the only reading on the
                page with the rules applied, and it belongs after the rules
                rather than between them and the control: read top to bottom the
                page is now weights, tiers, rules, result.

                A div, not a Card: `ModelPerformanceBands` renders its own, and
                wrapping it put a card inside a card. */}
            {shippedPerformances && (
              <div className="ftune-card">
                <ModelPerformanceBands
                  performances={shippedPerformances}
                  open={this.state.areShippedBandsOpen}
                  onToggleOpen={this.toggleShippedBands}
                  title="After overrides"
                  scope=""
                />
              </div>
            )}

            {/* Recomputing blanks the performances, so without this the section
                vanishes and reappears rather than saying it is working. */}
            {!shippedPerformances && loadingEnsemble && (
              <div className="ftune-card">
                <Spinner />
              </div>
            )}

            {!treesPage.trees?.length && isTreeBased && (
              <Error message="No trees found for this model." />
            )}
          </div>
        );
      }
    }
  )
);
